import { IPaymentOfferModel, isAnnualPeriod } from "@nf/common";
import { ROUTES } from "@nf/constants";
import React, { useEffect } from "react";
import { useTranslation } from "next-i18next";

import { useIsAuthenticated, useOffers, useRouter } from "hooks";
import { SimpleOfferCard } from "components";
import { VoucherLink } from "../VoucherLink/VoucherLink";
import { PaymentHeader } from "../PaymentHeader/PaymentHeader";
import { PaymentError } from "../PaymentError";
import { StepsProgressBar } from "../StepsProgressBar";
import type { IPaymentV2ScreenRouteProps } from "screens/PaymentScreen/PaymentScreen";

import styles from "./ChooseSubscription.module.scss";

export const ChooseSubscription = () => {
  const { isRegisteredViaCampaign } = useIsAuthenticated();
  const { t } = useTranslation("translations");
  const router = useRouter();
  const query = router.query as IPaymentV2ScreenRouteProps;

  const offerId = query.offerId;
  const error = query.error;

  const { offers } = useOffers();
  const hasOffers = !!offers?.length;

  const getSavingsText = (offer: IPaymentOfferModel) => {
    if (isRegisteredViaCampaign) {
      if (isAnnualPeriod(offer.Period)) {
        return t("web_choose_subscription_annual_save_aeldresagen");
      }

      return t("web_choose_subscription_monthly_save_aeldresagen");
    }

    if (isAnnualPeriod(offer.Period)) {
      return t("web_choose_subscription_annual_save");
    }

    return "";
  };

  const selectOffer = (offerId: number, offerHandle: string) => {
    router.push({
      pathname: ROUTES.PAYMENT_DETAILS,
      query: {
        offerId,
        offerHandle,
      },
    });
  };

  useEffect(() => {
    if (!offerId) {
      const defaultOffer = offers?.find((offer) => offer.DefaultOffer);
      if (defaultOffer) {
        router.replace(
          {
            pathname: ROUTES.PAYMENT_SUBSCRIPTION,
            query: {
              offerId: defaultOffer.Id,
              offerHandle: defaultOffer.Handle,
            },
          },
          undefined,
          {
            shallow: true,
          }
        );
      }
    }
  }, [offers, offerId]);

  return (
    <div className={styles.container}>
      <StepsProgressBar activeStep={3} />

      <PaymentHeader hasOffers={hasOffers} />

      {error && <PaymentError error={error} />}

      <div className={styles.offers}>
        {offers?.map((offer) => (
          <SimpleOfferCard
            key={offer.Id}
            offer={offer}
            active={offerId === `${offer.Id}`}
            savings={getSavingsText(offer)}
            handleSelect={selectOffer}
          />
        ))}
      </div>

      {hasOffers && !isRegisteredViaCampaign && <VoucherLink />}
    </div>
  );
};

export default ChooseSubscription;
